import axios, { AxiosInstance } from 'axios';

const DAEMON_URL = import.meta.env['VITE_DAEMON_URL'] ?? 'http://localhost:3200';
const BACKEND_URL = import.meta.env['VITE_BACKEND_URL'] ?? 'https://csms.magmatech.in';

const TOKEN_KEY = 'sim_admin_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// Two axios clients — one for the daemon, one for the main backend.
// The panel drives both: the backend owns the DB row, the daemon owns
// the WebSocket connection.
function withAuth(client: AxiosInstance): AxiosInstance {
  client.interceptors.request.use((cfg) => {
    const t = getToken();
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
  });
  client.interceptors.response.use(undefined, (err) => {
    if (err?.response?.status === 401) clearToken();
    return Promise.reject(err);
  });
  return client;
}

export const daemon = withAuth(axios.create({ baseURL: DAEMON_URL }));
export const backend = withAuth(axios.create({ baseURL: BACKEND_URL }));

/* ─── Types ──────────────────────────────────────────────────────── */

export interface SimulatorSnapshot {
  charger_id: string;
  port_count: number;
  connected: boolean;
  ws_state?: 'connecting' | 'open' | 'closing' | 'closed' | 'null';
  last_error?: string | null;
  consecutive_failures?: number;
  forced_offline: boolean;
  in_outage: boolean;
  outage_until: string | null;
  auto_sessions: boolean;
  ports: Array<{ port: number; is_charging: boolean; is_enabled: boolean; session_id: string | null }>;
  current_ma: number;
}

/* ─── Auth (uses main backend) ───────────────────────────────────── */

export async function login(email: string, password: string): Promise<{ token: string; role: string }> {
  const { data } = await backend.post('/api/auth/login', { email, password });
  if (!data.token) throw new Error('Login failed');
  return { token: data.token, role: data.user?.role ?? 'admin' };
}

/* ─── Daemon endpoints ───────────────────────────────────────────── */

export async function listSimulators(): Promise<SimulatorSnapshot[]> {
  const { data } = await daemon.get('/api/simulators');
  return data.simulators;
}

export async function createSimulatorOnDaemon(charger_id: string, port_count?: number): Promise<SimulatorSnapshot> {
  // port_count is optional — when omitted the daemon derives it from the
  // ports table. That's what makes "reattach an existing charger" work
  // without the panel needing to know how many ports it has.
  const body: { charger_id: string; port_count?: number } = { charger_id };
  if (port_count !== undefined) body.port_count = port_count;
  const { data } = await daemon.post('/api/simulators', body);
  return data.simulator;
}

export async function deleteSimulatorOnDaemon(charger_id: string): Promise<void> {
  await daemon.delete(`/api/simulators/${charger_id}`);
}

export async function setSimulatorOnline(charger_id: string, online: boolean): Promise<void> {
  await daemon.post(`/api/simulators/${charger_id}/online`, { online });
}

export async function forceStartPort(charger_id: string, port: number): Promise<void> {
  await daemon.post(`/api/simulators/${charger_id}/ports/${port}/start`);
}

export async function forceStopPort(charger_id: string, port: number): Promise<void> {
  await daemon.post(`/api/simulators/${charger_id}/ports/${port}/stop`);
}

export async function setAutoSessions(charger_id: string, enabled: boolean): Promise<void> {
  await daemon.post(`/api/simulators/${charger_id}/auto-sessions`, { enabled });
}

export async function setAutoSessionsAll(enabled: boolean): Promise<number> {
  const { data } = await daemon.post('/api/simulators/auto-sessions', { enabled });
  return data.updated ?? 0;
}

/* ─── Backend endpoints (charger row lifecycle) ──────────────────── */

export interface PortSpec {
  port_number: number;
  connector_type: string;
}

export interface CreateChargerPayload {
  charger_id: string;
  name: string;
  lat: number;
  lng: number;
  location?: string;
  vendor_id?: string;
  org_id?: string;
  power_kw?: number;
  is_public?: boolean;
  ports: PortSpec[];
}

export async function createChargerOnBackend(payload: CreateChargerPayload): Promise<void> {
  await backend.post('/api/chargers', payload);
}

/* ─── Vendors + Organizations (for dropdowns) ────────────────────── */

export interface VendorOption {
  id: string;
  business_name: string;
  org_id: string | null;
}

export interface OrgOption {
  id: string;
  name: string;
}

export async function getVendors(): Promise<VendorOption[]> {
  const { data } = await backend.get('/api/vendors');
  return (data as Array<{ id: string; business_name?: string | null; org_id?: string | null }>).map((v) => ({
    id: v.id,
    business_name: v.business_name ?? v.id,
    org_id: v.org_id ?? null,
  }));
}

export async function getOrganizations(): Promise<OrgOption[]> {
  const { data } = await backend.get('/api/organizations');
  return (data as Array<{ id: string; name?: string | null }>).map((o) => ({
    id: o.id,
    name: o.name ?? o.id,
  }));
}

export async function deleteChargerOnBackend(charger_id: string): Promise<void> {
  await backend.delete(`/api/chargers/${charger_id}`);
}

export interface ChargerPortRow {
  id: string;
  port_number: number;
  connector_type: string;
  is_enabled: boolean;
  is_charging: boolean;
}

export interface ChargerRow {
  charger_id: string;
  name: string | null;
  location: string | null;
  lat: number | string | null;
  lng: number | string | null;
  power_kw: number | null;
  is_public: boolean;
  vendor_id: string | null;
  org_id:    string | null;
  ports:     ChargerPortRow[];
}

export async function getChargerFromBackend(charger_id: string): Promise<ChargerRow> {
  const { data } = await backend.get(`/api/chargers/${charger_id}`);
  return data;
}

export interface UpdateChargerPatch {
  name?:      string;
  location?:  string;
  lat?:       number;
  lng?:       number;
  power_kw?:  number;
  is_public?: boolean;
  vendor_id?: string | null;
  org_id?:    string | null;
}

export async function updateChargerOnBackend(charger_id: string, patch: UpdateChargerPatch): Promise<void> {
  await backend.patch(`/api/chargers/${charger_id}`, patch);
}

// Per-port endpoints (mirror the admin panel — the backend already
// supports add/delete/patch for ports, so the simulator UI just calls
// them one-at-a-time from the Edit modal's diff logic).
export async function addPortOnBackend(
  charger_id: string,
  data: { port_number?: number; connector_type?: string },
): Promise<void> {
  await backend.post(`/api/chargers/${charger_id}/ports`, data);
}

export async function deletePortOnBackend(charger_id: string, portNum: number): Promise<void> {
  await backend.delete(`/api/chargers/${charger_id}/ports/${portNum}`);
}

export async function updatePortOnBackend(
  charger_id: string,
  portNum: number,
  data: { connector_type?: string; is_enabled?: boolean },
): Promise<void> {
  await backend.patch(`/api/chargers/${charger_id}/ports/${portNum}`, data);
}
